/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { css, jsx } from '@atlaskit/css';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingIndicator } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingIndicator';
import type { RovoThinkingMinimizationProps } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingMinimization';
import { RovoThinkingPill } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingPill';

import type { EditorAgent } from '../../../utils/agents';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import LoadingIcon from '../../assets/Loading';
import { CancelEscButton } from '../../components/CancelEscButton/CancelEscButton';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Footer } from '../../components/Footer/Footer';
import type { Disclaimer } from '../../components/Footer/types';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';
import { Breakpoint, useElementBreakpoints } from '../../hooks/useElementBreakpoints';
import messages from './messages';
import { getLoadingTitle } from './utils';

const LazyRovoThinkingMinimization = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/rovo-platform-ui-components/rovo-thinking-minimization" */ '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingMinimization'
	).then(({ RovoThinkingMinimization }) => ({ default: RovoThinkingMinimization })),
);

const disabledScrollMinimization = { isEnabled: false };
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type MaybeMinimizableProps = {
	children: JSX.Element;
	/**
	 * When false the children are rendered untouched, so the bar renders exactly
	 * as it does today outside the cohort.
	 */
	isEnabled?: boolean;
	isMinimized?: boolean;
	onMinimizedChange?: (isMinimized: boolean) => void;
	pillProps: RovoThinkingMinimizationProps['pillProps'];
	scrollMinimization: RovoThinkingMinimizationProps['scrollMinimization'];
};

/**
 * Optional scroll/auto minimization around the thinking bar. Declared at module
 * scope and rendered as JSX so React keeps a stable identity for the lazy
 * component across re-renders, instead of re-suspending and losing its state.
 */
const MaybeMinimizable = ({
	children,
	isEnabled,
	pillProps,
	scrollMinimization,
	isMinimized: isMinimizedProp,
	onMinimizedChange,
}: MaybeMinimizableProps): JSX.Element => {
	const [internalIsMinimized, setInternalIsMinimized] = useState(false);
	const isControlled = isMinimizedProp !== undefined;
	const isMinimized = isMinimizedProp ?? internalIsMinimized;
	const {
		scrollContainer,
		isEnabled: isScrollEnabled = true,
		shouldMinimize,
	} = scrollMinimization ?? {};
	const latestScrollState = useRef({ isControlled, onMinimizedChange, shouldMinimize });
	useIsomorphicLayoutEffect(() => {
		latestScrollState.current = { isControlled, onMinimizedChange, shouldMinimize };
	}, [isControlled, onMinimizedChange, shouldMinimize]);
	const setIsMinimized = useCallback((nextIsMinimized: boolean) => {
		const { isControlled, onMinimizedChange } = latestScrollState.current;
		if (isControlled) {
			onMinimizedChange?.(nextIsMinimized);
			return;
		}
		setInternalIsMinimized(nextIsMinimized);
	}, []);
	useEffect(() => {
		if (!isEnabled || !isScrollEnabled) {
			return;
		}
		// Track scrolls outside Suspense so the fallback and loaded bar share their state.
		return bind(scrollContainer ?? window, {
			type: 'scroll',
			listener: () => {
				if (latestScrollState.current.shouldMinimize?.() ?? true) {
					setIsMinimized(true);
				}
			},
			// Capture runs before the editor clears its programmatic-scroll guard.
			options: { capture: true, passive: true },
		});
	}, [isEnabled, isScrollEnabled, scrollContainer, setIsMinimized]);
	const onPillClick = useCallback(() => {
		setIsMinimized(false);
	}, [setIsMinimized]);
	const { status, label, testId } = pillProps ?? {};

	if (!isEnabled) {
		return children;
	}

	return (
		<React.Suspense
			fallback={
				isMinimized ? (
					<RovoThinkingPill label={label} onClick={onPillClick} status={status} testId={testId} />
				) : (
					children
				)
			}
		>
			<LazyRovoThinkingMinimization
				autoMinimizeDelayMs={null}
				pillProps={pillProps}
				scrollMinimization={disabledScrollMinimization}
				isMinimized={isMinimized}
				onMinimizedChange={setIsMinimized}
			>
				{children}
			</LazyRovoThinkingMinimization>
		</React.Suspense>
	);
};

const defaultPaddingStyles = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.150'),
});

const loadingPaddingStyles = css({
	paddingTop: token('space.150'),
	paddingBottom: token('space.150'),
	paddingLeft: token('space.200'),
	paddingRight: token('space.200'),
});

const chromelessLoadingPaddingStyles = css({
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
});

const defaultPaddingNoInlineStreamChangesStyles = css({
	paddingTop: token('space.150'),
	paddingLeft: token('space.150'),
	paddingRight: token('space.150'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const previewContainer = cssUnbounded({
	borderTop: `${token('border.width')} solid ${token('color.border')}`,
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
});

const layoutStyles = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'left',
	alignItems: 'center',
	flexWrap: 'wrap',
});

const layoutStylesPatchGa = css({
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const logoStyles = cssUnbounded({
	marginRight: token('space.025'),
	display: 'flex',
	alignItems: 'center',
});

const titleLayoutStyles = css({ flex: 1 });
const commonTextStyles = css({
	font: token('font.body'),
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const titleStyles = [titleLayoutStyles, commonTextStyles];

const previewLoadingScreenStyles = css({
	paddingTop: token('space.075'),
});
/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */
type LatestPrompt =
	| { presetTitle?: never; userADFInput?: JSONDocNode; userInput: string }
	| { presetTitle: string; userADFInput?: JSONDocNode; userInput?: string };

type Props = {
	additionalFooterText?: string;
	agent?: EditorAgent;
	appearance?: EditorAppearance;
	canInlineStreamChanges?: boolean;
	/**
	 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	childShortTitle?: string;
	disclaimer?: Disclaimer;
	/**
	 * Hide the loading screen if true
	 */
	hideLoadingScreen?: boolean;
	isAIFC?: boolean;
	isLoadingBarCollapseEnabled?: boolean;
	isLoadingBarMinimized?: boolean;
	isStreaming?: boolean;
	latestPrompt?: LatestPrompt;
	/**
	 * Selects an alternative loading-state UI. When 'rovo-thinking-bar' and isAIFC is set,
	 * the unified RovoThinkingBar is rendered instead of the default thinking indicator +
	 * cancel button. Propagated from the AI config item (see editor-plugin-ai).
	 */
	loadingExperience?: 'rovo-thinking-bar';
	onCancel: () => void;
	onLoadingBarMinimizedChange?: (isMinimized: boolean) => void;
	/**
	 * Label for the parent if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
	 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
	 */
	parentPresetPromptLabel?: string;
	/**
	 * Label for the selected partial prompt.
	 */
	presetPromptLabel?: string;
	/**
	 * Pass undefined when there is no previewable content
	 */
	preview: React.ReactElement | undefined;
	providerFactory: ProviderFactory;
	schema: Schema;
	/**
	 * Scroll container whose scrolling collapses the thinking bar into its pill.
	 * Should be the editor's overflow-scroll ancestor; falls back to the window.
	 */
	scrollContainer?: HTMLElement | Window;
	shouldMinimizeOnScroll?: () => boolean;
	title: string;
};

export const LoadingScreen = ({
	parentPresetPromptLabel,
	childShortTitle,
	title,
	latestPrompt,
	preview,
	providerFactory,
	schema,
	onCancel,
	onLoadingBarMinimizedChange,
	agent,
	disclaimer,
	isStreaming,
	canInlineStreamChanges,
	hideLoadingScreen,
	appearance,
	isAIFC,
	isLoadingBarCollapseEnabled,
	isLoadingBarMinimized,
	loadingExperience,
	scrollContainer,
	shouldMinimizeOnScroll,
}: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();

	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints(Breakpoint.SmallChromeless);

	const setRootContainer = (element: HTMLDivElement | null) => {
		setBreakpointsElement(element);
	};

	const pillProps = React.useMemo(
		() => ({ status: isStreaming ? ('editing' as const) : ('analyzing' as const) }),
		[isStreaming],
	);
	// `isEnabled` is left to its default: this is only ever passed from `MaybeMinimizable`,
	// which already requires the experiment, the AIFC inline-streaming branch and a visible
	// loading screen.
	const scrollMinimization = React.useMemo(
		() => ({
			scrollContainer,
			shouldMinimize: shouldMinimizeOnScroll,
		}),
		[scrollContainer, shouldMinimizeOnScroll],
	);

	const CommonFooter = ({
		agent,
		disclaimer,
	}: {
		agent?: EditorAgent;
		disclaimer?: Disclaimer;
	}) => (
		<Footer
			brand={agent ? 'rovo' : 'atlassian-intelligence'}
			disclaimer={disclaimer}
			showDisclaimer={true}
			isAIFC={isAIFC}
		/>
	);

	const LoadingAnimationSection = React.useMemo(
		() => (
			<div
				css={[
					isAIFC
						? appearance === 'chromeless'
							? chromelessLoadingPaddingStyles
							: loadingPaddingStyles
						: defaultPaddingStyles,
					layoutStyles,
					layoutStylesPatchGa,
				]}
			>
				{isAIFC ? (
					loadingExperience === 'rovo-thinking-bar' ? (
						/**
						 * Create with Rovo blank-object experience: the unified RovoThinkingBar
						 * provides its own thinking indicator (with rotating titles), a rainbow
						 * progress bar, and a stop button.
						 */
						<RovoThinkingBar
							status="analyzing"
							onStop={onCancel}
							useRotatingTitle
							showProgressBar
						/>
					) : (
						/**
						 * If aifc is on, we show the new loading modal
						 */
						<React.Fragment>
							<RovoThinkingIndicator
								title={
									isStreaming
										? breakpoint <= Breakpoint.SmallChromeless
											? formatMessage(messages.inlineCommentLoadingEditingTitle)
											: getLoadingTitle(latestPrompt?.presetTitle, formatMessage)
										: formatMessage(
												breakpoint <= Breakpoint.SmallChromeless
													? messages.inlineCommentLoadingAnalyzing
													: messages.loadingAnalyzing,
											)
								}
								icon={true}
								animation={true}
							/>
							<CancelEscButton onCancel={onCancel} isAIFC />
						</React.Fragment>
					)
				) : (
					/**
					 * Old loading modal
					 */
					<React.Fragment>
						<div data-testid="loading-icon" css={logoStyles}>
							<LoadingIcon alt={formatMessage(messages.iconAltText)} />
						</div>
						<div css={titleStyles}>{title}</div>
						<CancelEscButton autoFocus onCancel={onCancel} isAIFC={isAIFC} />
					</React.Fragment>
				)}
			</div>
		),
		[
			isStreaming,
			breakpoint,
			formatMessage,
			latestPrompt?.presetTitle,
			onCancel,
			title,
			isAIFC,
			appearance,
			loadingExperience,
		],
	);

	const LiveRegion = ({ preview }: { preview: React.ReactElement | undefined }) => {
		return (
			<React.Fragment>
				<VisuallyHidden>{formatMessage(messages.loadingAriaTitle)}</VisuallyHidden>
				{/**
				 * we will be passing userADFInput here,
				 *  when LatestPromptHeader starts using Renderer.
				 * Will be done in https://product-fabric.atlassian.net/browse/EDF-367
				 */}
				{preview &&
					(isAIFC ? (
						<div
							data-testid="adf-content-live-region"
							aria-atomic="false"
							aria-live="polite"
							css={[defaultPaddingNoInlineStreamChangesStyles]}
							/* eslint-enable */
						>
							<VisuallyHidden testId="description">
								{formatMessage(messages.liveRegionDescription)}
							</VisuallyHidden>
							<div css={[previewLoadingScreenStyles]}>{preview}</div>
						</div>
					) : (
						<div
							data-testid="adf-content-live-region"
							aria-atomic="false"
							aria-live="polite"
							css={[previewContainer, defaultPaddingStyles]}
							/* eslint-enable */
						>
							<VisuallyHidden testId="description">
								{formatMessage(messages.liveRegionDescription)}
							</VisuallyHidden>
							<div>{preview}</div>
						</div>
					))}
			</React.Fragment>
		);
	};

	if (hideLoadingScreen && canInlineStreamChanges && isAIFC) {
		return null;
	}

	if (canInlineStreamChanges && isAIFC) {
		return (
			<FloatingContainer
				ref={setRootContainer}
				data-testid="loading-screen"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={`loading-screen ${AI_MODAL_SCREEN_CLASS}`}
				rainbowBorder={false}
				disablePadding
				enableMaxWidth
				appearance={appearance}
				isAIFC={isAIFC}
				isPositionalOnly
			>
				<MaybeMinimizable
					isEnabled={isLoadingBarCollapseEnabled}
					pillProps={pillProps}
					scrollMinimization={scrollMinimization}
					isMinimized={isLoadingBarMinimized}
					onMinimizedChange={onLoadingBarMinimizedChange}
				>
					<RovoThinkingBarWrapper isThinking>
						<RovoThinkingBar
							status={isStreaming ? 'editing' : 'analyzing'}
							onStop={onCancel}
							useShortMessages={breakpoint <= Breakpoint.SmallChromeless}
							useRotatingTitle={loadingExperience === 'rovo-thinking-bar'}
							showProgressBar={loadingExperience === 'rovo-thinking-bar'}
						/>
					</RovoThinkingBarWrapper>
				</MaybeMinimizable>
			</FloatingContainer>
		);
	}

	return (
		<FloatingContainer
			ref={setRootContainer}
			data-testid="loading-screen"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={`loading-screen ${AI_MODAL_SCREEN_CLASS}`}
			rainbowBorderAnimation={!isAIFC}
			rainbowBorder={!isAIFC}
			rovoGradientBorder={isAIFC && expValEquals('cc-maui-experiment', 'isEnabled', true)}
			disablePadding
			enableMaxWidth={isAIFC && canInlineStreamChanges}
			appearance={appearance}
			isAIFC={isAIFC}
		>
			{isAIFC ? (
				canInlineStreamChanges ? (
					/**
					 * inline streaming route
					 */
					!hideLoadingScreen && LoadingAnimationSection
				) : (
					/**
					 * fall back modal
					 */
					<div>
						<LiveRegion preview={preview} />
						{LoadingAnimationSection}
						<CommonFooter agent={agent} disclaimer={disclaimer} />
					</div>
				)
			) : (
				<div>
					<VisuallyHidden>{formatMessage(messages.loadingAriaTitle)}</VisuallyHidden>
					{/**
					 * we will be passing userADFInput here,
					 *  when LatestPromptHeader starts using Renderer.
					 * Will be done in https://product-fabric.atlassian.net/browse/EDF-367
					 */}
					<LatestPromptHeader
						parentPresetPromptLabel={parentPresetPromptLabel}
						childShortTitle={childShortTitle}
						prompt={latestPrompt?.userInput}
						promptADF={latestPrompt?.userADFInput}
						presetTitle={latestPrompt?.presetTitle}
						providerFactory={providerFactory}
						schema={schema}
						agent={agent}
						showBack={true}
					/>
					<LiveRegion preview={preview} />
					{LoadingAnimationSection}
					<CommonFooter agent={agent} disclaimer={disclaimer} />
				</div>
			)}
		</FloatingContainer>
	);
};

export const MemoLoadingScreen: React.MemoExoticComponent<
	({
		parentPresetPromptLabel,
		childShortTitle,
		title,
		latestPrompt,
		preview,
		providerFactory,
		schema,
		onCancel,
		agent,
		disclaimer,
		isStreaming,
		canInlineStreamChanges,
		hideLoadingScreen,
		appearance,
		isAIFC,
		loadingExperience,
	}: Props) => JSX.Element | null
> = React.memo(LoadingScreen);
